/**
 * Map Multiple Block JS - Renders Google Map with multiple markers
 */
import '../../scss/acf-blocks/acfb-map-multiple.scss';

// Traccia l'infoWindow attualmente aperto
let mapCurrentOpenInfoWindow = null;

// Il pulsante X nativo dell'InfoWindow (.gm-ui-hover-effect) viene nascosto via
// CSS (vedi acfb-map-multiple.scss). Sostituisce il vecchio MutationObserver su
// document.body/subtree, che osservava OGNI mutazione DOM dell'intera pagina.

/**
 * Escapes a string for safe insertion into HTML context.
 * Uses a DOM text node to prevent XSS via innerHTML.
 *
 * @param {string} str - The string to escape
 * @returns {string} HTML-escaped string
 */
function escHtml(str) {
	if (!str) return '';
	const div = document.createElement('div');
	div.appendChild(document.createTextNode(String(str)));
	return div.innerHTML;
}

/**
 * Crea un tooltip (InfoWindow) per il marker con dati opzionali
 * Il tooltip si mostra al click sul marker con stili inline via JS
 *
 * @param {google.maps.Marker} marker - Il marker a cui aggiungere il tooltip
 * @param {Object} tooltipData - Dati opzionali per il tooltip { title, permalink, ctaText }
 */
function createMapTooltip(marker, tooltipData = {}) {
	// Se non ci sono dati o non c'è titolo, non creamo il tooltip
	if (!tooltipData || !tooltipData.title) {
		return null;
	}

	// Testo CTA traduzibile (di default "Scopri di più")
	const ctaText = tooltipData.ctaText || window.celadaMap?.ctaText || 'Scopri di più';

	// Stili inline per il tooltip container
	const tooltipStyles = `
		// padding: 12px 1em;
		// background-color: rgba(0, 0, 0, 0.85);
		// color: #fff;
		// border-radius: 4px;
		// font-size: 14px;
		// line-height: 1.5;
		// min-width: 150px;
		// max-width: 300px;
		// word-wrap: break-word;
		// white-space: normal;
	`;

	// Stili inline per il titolo
	const titleStyles = `
		font-weight: 600;
		margin-bottom: 8px;
		margin: 0;
		padding: 0;
		color:var(--wp--preset--color--black, #000);
	`;

	// Stili inline per il link
	const linkStyles = `
		color:var(--wp--preset--color--primary, #000);
		text-decoration: none;
		display: inline-block;
		margin-top: 0.5em;
		font-size: 12px;
		font-weight: 500;
		transition: all 0.2s ease;
		cursor: pointer;
	`;

	// Costruisci l'HTML del tooltip con stili inline
	let tooltipContent = `
		<div style="${tooltipStyles}">
			<div style="${titleStyles}">${escHtml(tooltipData.title)}</div>
	`;

	// Se c'è un permalink, aggiungi il link CTA
	if (tooltipData.permalink) {
		tooltipContent += `
			<a href="${escHtml(tooltipData.permalink)}" style="${linkStyles}" target="_blank" rel="noopener noreferrer">${escHtml(ctaText)}</a>
		`;
	}

	tooltipContent += `
		</div>
	`;

	// Crea l'InfoWindow secondo la documentazione ufficiale di Google Maps
	const infoWindow = new google.maps.InfoWindow({
		content: tooltipContent,
		disableAutoPan: true,
		maxWidth: 300,
	});

	// Al click sul marker, apri il tooltip
	marker.addListener('click', () => {
		// Chiudi il tooltip precedente se aperto
		if (mapCurrentOpenInfoWindow) {
			mapCurrentOpenInfoWindow.close();
		}

		// Apri il nuovo tooltip
		infoWindow.open({
			anchor: marker,
			shouldFocus: false,
		});

		// Traccia il tooltip aperto
		mapCurrentOpenInfoWindow = infoWindow;

		// Auto-chiudi il tooltip dopo 15 secondi
		setTimeout(() => {
			if (infoWindow && mapCurrentOpenInfoWindow === infoWindow) {
				infoWindow.close();
				mapCurrentOpenInfoWindow = null;
			}
		}, 15000);
	});

	return infoWindow;
}

function initializeMapMultiple(mapElement) {
	const markersJson = mapElement.dataset.markers;
	const centerLat = parseFloat(mapElement.dataset.centerLat);
	const centerLng = parseFloat(mapElement.dataset.centerLng);
	const zoom = parseInt(mapElement.dataset.zoom) || 7;

	if (!markersJson) {
		console.error('No markers data found');
		return;
	}

	let markers;
	try {
		markers = JSON.parse(markersJson);
	} catch (error) {
		console.error('Failed to parse markers JSON:', error);
		return;
	}

	if (!Array.isArray(markers) || markers.length === 0) {
		console.error('Markers array is empty');
		return;
	}

	if (isNaN(centerLat) || isNaN(centerLng)) {
		console.error('Invalid center coordinates');
		return;
	}

	if (!window.google || !window.google.maps) {
		console.error('Google Maps API not loaded');
		return;
	}

	const mapCenter = { lat: centerLat, lng: centerLng };

	// Google Maps Grayscale Theme
	const mapStyles = [
		{
			elementType: 'geometry',
			stylers: [{ color: '#f5f5f5' }],
		},
		{
			elementType: 'labels.icon',
			stylers: [{ visibility: 'off' }],
		},
		{
			elementType: 'labels.text.fill',
			stylers: [{ color: '#616161' }],
		},
		{
			elementType: 'labels.text.stroke',
			stylers: [{ color: '#f5f5f5' }],
		},
		{
			featureType: 'administrative.land_parcel',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#bdbdbd' }],
		},
		{
			featureType: 'poi',
			elementType: 'geometry',
			stylers: [{ color: '#eeeeee' }],
		},
		{
			featureType: 'poi',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#757575' }],
		},
		{
			featureType: 'poi.park',
			elementType: 'geometry',
			stylers: [{ color: '#e5e5e5' }],
		},
		{
			featureType: 'poi.park',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#9e9e9e' }],
		},
		{
			featureType: 'road',
			elementType: 'geometry',
			stylers: [{ color: '#ffffff' }],
		},
		{
			featureType: 'road.arterial',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#757575' }],
		},
		{
			featureType: 'road.highway',
			elementType: 'geometry',
			stylers: [{ color: '#dadada' }],
		},
		{
			featureType: 'road.highway',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#616161' }],
		},
		{
			featureType: 'road.local',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#9e9e9e' }],
		},
		{
			featureType: 'transit.line',
			elementType: 'geometry',
			stylers: [{ color: '#e5e5e5' }],
		},
		{
			featureType: 'transit.station',
			elementType: 'geometry',
			stylers: [{ color: '#eeeeee' }],
		},
		{
			featureType: 'water',
			elementType: 'geometry',
			stylers: [{ color: '#c9c9c9' }],
		},
		{
			featureType: 'water',
			elementType: 'labels.text.fill',
			stylers: [{ color: '#9e9e9e' }],
		},
	];

	try {
		const map = new google.maps.Map(mapElement, {
			zoom,
			center: mapCenter,
			disableDefaultUI: true,
			gestureHandling: 'none',
			scrollwheel: false,
			styles: mapStyles,
		});

		// Use LatLngBounds to properly center and fit all markers
		const bounds = new google.maps.LatLngBounds();

		// Add markers for each location
		markers.forEach((markerData) => {
			const markerPosition = {
				lat: markerData.lat,
				lng: markerData.lng,
			};

			const markerOptions = {
				position: markerPosition,
				map,
				title: markerData.title,
				cursor: 'default',
			};

			if (window.celadaMap && window.celadaMap.markerIcon) {
				markerOptions.icon = {
					url: window.celadaMap.markerIcon,
					scaledSize: new google.maps.Size(33, 42),
				};
			}

			const marker = new google.maps.Marker(markerOptions);

			// Predisponi il tooltip con title e permalink
			const tooltipData = {
				title: markerData.title,
				permalink: markerData.permalink,
			};
			createMapTooltip(marker, tooltipData);

			bounds.extend(markerPosition);
		});

		// Fit map to show all markers
		map.fitBounds(bounds);

		// If a custom zoom is set, apply it after fitBounds
		if (zoom > 0) {
			google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
				map.setZoom(zoom);
			});
		}

		mapElement.dataset.mapInit = '1';
	} catch (error) {
		console.error('Map error:', error);
	}
}

function initMapMultiples() {
	const elements = document.querySelectorAll('.acf-map-multiple');
	elements.forEach((el) => {
		if (el.dataset.mapInit !== '1') {
			initializeMapMultiple(el);
		}
	});
}

function init() {
	if (window.google && window.google.maps) {
		initMapMultiples();
	} else {
		setTimeout(init, 100);
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}

if (window.acf) {
	acf.addAction('render_block_preview', () => {
		setTimeout(initMapMultiples, 100);
	});
}
